import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';

import Scenes   from './Scenes4';
import Lightbox from './Lightbox5';
import { PageHeader } from './ShortlistShared3';
import { RoomTabStrip, useRoomRemoval } from './RoomTabs';
import { apiFetch, friendlyMessage } from '../../../api/apiClient';
import { getTokenSubject } from '../../../auth/token';
import { LoadingState, ErrorState, SkeletonGrid, useToast } from '../../../shared/components/status';

import { appGridBg, PAGE_PX } from '../../../theme/tokens2';

const sortScenes = (data) =>
  [...data].sort((a, b) => {
    if (a.cat_room === b.cat_room) return a.ranking - b.ranking;
    return (a.cat_room || '').localeCompare(b.cat_room || '');
  });

// ─── Moodboarding ─────────────────────────────────────────────────────────────
export default function Moodboarding() {
  const toast = useToast();
  const phoneNumber = getTokenSubject(); // the signed-in customer's phone number

  const [rooms,        setRooms]        = useState([]);   // { id, room_name }[]
  const [selectedRoom, setSelectedRoom] = useState('');
  const [roomsStatus,  setRoomsStatus]  = useState('loading'); // 'loading' | 'ready' | 'error'
  const [roomsError,   setRoomsError]   = useState('');

  const [scenesData,   setScenesData]   = useState([]);
  const [scenesStatus, setScenesStatus] = useState('loading');
  const [scenesError,  setScenesError]  = useState('');

  // Lightbox state
  const [lightboxOpen,         setLightboxOpen]         = useState(false);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0);

  // Rooms (the server adds three default rooms the first time)
  const fetchRooms = useCallback(async () => {
    setRoomsStatus('loading');
    try {
      const data = await apiFetch('/api/rooms/my');
      setRooms(data || []);
      setSelectedRoom((prev) => prev || data?.[0]?.room_name || '');
      setRoomsStatus('ready');
    } catch (err) {
      setRoomsError(friendlyMessage(err));
      setRoomsStatus('error');
    }
  }, []);

  // All photos, across every room
  const fetchScenes = useCallback(async () => {
    setScenesStatus((s) => (s === 'ready' ? 'ready' : 'loading')); // refreshes keep the grid on screen
    try {
      const data = await apiFetch('/api/scenes/my');
      setScenesData(sortScenes(data || []));
      setScenesStatus('ready');
    } catch (err) {
      setScenesError(friendlyMessage(err));
      setScenesStatus('error');
    }
  }, []);

  useEffect(() => { fetchRooms(); fetchScenes(); }, [fetchRooms, fetchScenes]);

  // Add a new room straight away
  const handleAddRoom = async (roomName) => {
    try {
      const newRoom = await apiFetch('/api/rooms', { method: 'POST', body: { room_name: roomName } });
      setRooms((prev) => {
        // The server returns the existing room if the name is already used.
        if (prev.some((r) => r.id === newRoom.id)) return prev;
        return [...prev, newRoom];
      });
      setSelectedRoom(newRoom.room_name);
    } catch (err) {
      toast.error(`We couldn't add "${roomName}". Please try again.`);
    }
  };

  // Removing a room (the × on its tab). Its photos leave this page too.
  const roomRemoval = useRoomRemoval({
    rooms, setRooms, selectedRoom, setSelectedRoom,
    onRoomRemoved: (room) => setScenesData((prev) => prev.filter((s) => s.cat_room !== room.room_name)),
  });

  // Photos for the selected room
  const currentItems = scenesData
    .filter((item) => item.cat_room === selectedRoom)
    .sort((a, b) => a.ranking - b.ranking);

  const handleItemsChanged = (updatedRoomItems) => {
    setScenesData((prev) => {
      const others = prev.filter((item) => item.cat_room !== selectedRoom);
      return sortScenes([...others, ...updatedRoomItems]);
    });
  };

  const handleOpenLightbox = (index) => {
    setLightboxInitialIndex(index);
    setLightboxOpen(true);
  };

  // ── Loading / error ────────────────────────────────────────────────────────

  if (roomsStatus === 'loading') {
    return <LoadingState label="Loading your moodboard…" size="page" />;
  }

  if (roomsStatus === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load your moodboard"
        message={roomsError}
        onRetry={() => { fetchRooms(); fetchScenes(); }}
      />
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden', ...appGridBg,
    }}>
      {/* ── Page header ── */}
      <PageHeader
        title="Show us your"
        accent="style."
        description="Found a photo you love, or made one with AI? Upload it here. We'll search the market for lookalike tiles, vinyl, laminates, wall paint and furniture within 24 hours."
        note="Upload by 12pm, and we'll tag the lookalikes on your photos by 12pm the next day."
      >
        <RoomTabStrip
          rooms={rooms}
          selectedRoom={selectedRoom}
          onSelectRoom={setSelectedRoom}
          onAddRoom={handleAddRoom}
          onRemoveRoom={roomRemoval.requestRemove}
          checkingId={roomRemoval.checkingId}
        />
      </PageHeader>

      {/* ── Photo grid ── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {scenesStatus === 'loading' && (
          <Box sx={{ py: '20px', px: PAGE_PX }}>
            <SkeletonGrid count={4} aspectRatio="4/5" minColumnWidth={200} label="Loading your photos…" />
          </Box>
        )}

        {scenesStatus === 'error' && (
          <ErrorState
            title="We couldn't load your photos"
            message={scenesError}
            onRetry={fetchScenes}
          />
        )}

        {scenesStatus === 'ready' && (
          <Scenes
            items={currentItems}
            selectedCategory={selectedRoom}
            phoneNumber={phoneNumber}
            onOpenLightbox={handleOpenLightbox}
            onItemsChanged={handleItemsChanged}
            onRefetch={fetchScenes}
          />
        )}
      </Box>

      {roomRemoval.dialog}

      {/* ── Lightbox overlay ── */}
      {lightboxOpen && (
        <Lightbox
          photos={currentItems}
          initialPhotoIndex={lightboxInitialIndex}
          selectedCategory={selectedRoom}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </Box>
  );
}
